/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable class-methods-use-this */
import {
  AppliedFacets,
  OrderCriteria,
  PageNumberCriteria,
  FacetCriteria,
  FacetsInOrderCriteria,
  CatalogParams,
  PlpCatalog,
  PlpProduct,
  Facets,
  PlpFacet,
  AllFacets,
  PlpFilterType,
  PlpFilterLayout,
} from '@/types/plp'
import wcs from '@/scripts/libs/wcs'
import { ProductListPageHelper } from '@/scripts/libs/plp'
import { AlgoliaProductHit, PriceRange, FacetStatsRecordD } from '@/scripts/libs/algolia/types/plp'
import Algolia, { SearchResponse } from '@/scripts/libs/algolia'
import qs from 'qs'

import AlgoliaFacetsUtils from '@/scripts/libs/algolia/facets'
import AlgoliaAnalytics from '@/scripts/libs/algolia/analytics'
import AlgoliaProductsUtils from '@/scripts/libs/algolia/products'
import AlgoliaPromoToggles from '@/scripts/libs/algolia/promo-toggles'
import { Url } from '@/scripts/libs/url'

import { SEARCH_TERM_QUERY_PARAM } from '@/scripts/constants/search'
import axios from 'axios'
import { TRUE_LOWERCASE } from '@/scripts/constants/booleans'
import {
  CAN_SHOW_PLP_ACTIONS,
  COLOR_FACETS,
  FACET_KEYS,
  HITS_PER_PAGE_DESKTOP,
  PRICE_FILTER,
  VERTICAL_FACETS,
} from '@/scripts/constants/plp'

export class AlgoliaPlp implements ProductListPageHelper {
  private allFacets: undefined | Facets = undefined
  private priceRange: undefined | PriceRange = undefined
  private initialProductHits: Array<AlgoliaProductHit> = []
  private totalProductsCount = 0
  private isInitialized = false
  private hitsPerPageDesk = HITS_PER_PAGE_DESKTOP
  private hitsPerPageMob = 24
  private hitsPerPage = this.hitsPerPageDesk
  private firstCallResult: undefined | SearchResponse<AlgoliaProductHit>
  private readonly ruleContext?: string = undefined
  private facetStats: undefined | FacetStatsRecordD
  private promoFiltersApplied: {
    sortBadge_Guest?: { [k: string]: number }
    sortBadge_early_access?: { [k: string]: number }
  } = {}
  /* TODO: since we're using Algolia's localStorage cache, we do not need this anymore */
  constructor(ruleContext?: string) {
    this.ruleContext = ruleContext
  }

  public static generateModule() {
    if (window.algoliaPlp) {
      return window.algoliaPlp
    }

    return new AlgoliaPlp()
  }

  getAppliedFacets = (): AppliedFacets => {
    const queryParams = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    })
    if (!queryParams.facet) {
      return []
    }
    const result = Array.isArray(queryParams.facet)
      ? (queryParams.facet as string[])
      : ([queryParams.facet] as string[])
    const test = result.map((facet) => Url.decodeFacet(facet))
    return test || []
  }

  getAppliedOrderCriteria(): string | undefined {
    const queryParams = new URLSearchParams(window.location.search)
    return queryParams.get('orderBy') ?? undefined
  }

  mapOrderCriteria = (value: null | string): OrderCriteria => {
    return {
      orderBy: value,
    }
  }

  mapPageNumberCriteria = (value: string): PageNumberCriteria => ({
    pageNumber: value,
  })

  mapFacetCriteria = (facets: AppliedFacets): FacetCriteria => ({
    facet: facets,
  })

  mapFacetsInOrderCriteria(_: AppliedFacets): FacetsInOrderCriteria {
    return { facetsInOrder: '' }
  }

  async fetchAppliedFacetsRecap(
    appliedFacets: AppliedFacets
  ): Promise<{ [k: string]: string | number }> {
    if (!this.isInitialized) {
      await this.init()
      /* TODO: Translate this */
      return { resourceId1: '', resultCount: this.totalProductsCount, resultLabel: 'Results' }
    }

    const sanitizedAppliedFacets = this.sanitizeAppliedFacets(appliedFacets)

    const facetFilters = AlgoliaFacetsUtils.mergeFacetsForAlgoliaSearch(sanitizedAppliedFacets)
    const query = this.getSearchTerm() ?? ''

    const searchResult = await Algolia.groupedSearch<AlgoliaProductHit>({
      options: {
        query,
        attributesToRetrieve: [],
        hitsPerPage: 1,
        facetFilters,
        filters: AlgoliaFacetsUtils.getCategoryFacet(),
        numericFilters: [
          ...AlgoliaFacetsUtils.applyPriceFilter(appliedFacets, this.priceRange?.max),
          ...AlgoliaFacetsUtils.applyHingeDistanceFilter(appliedFacets),
        ],
      },
    })

    /* TODO: Translate this */
    return { resourceId1: '', resultCount: searchResult.nbHits, resultLabel: 'Results' }
  }

  initialResponse = async (appliedOrderBy?: string) => {
    const result = await Algolia.groupedSearch<AlgoliaProductHit>({
      query: this.getSearchTerm(),
      options: {
        ruleContexts: this.getRuleContexts(this.ruleContext),
        clickAnalytics: true,
        hitsPerPage: this.hitsPerPage,
        attributesToRetrieve: ['*'],
        facetingAfterDistinct: true,
        facets: ['attributes_translated.*', 'sort*', 'priceFilter*'],
        filters: AlgoliaFacetsUtils.getCategoryFacet(),
      },
    })

    this.isInitialized = true

    const objectIDs = result.hits.map((hit) => hit.objectID)
    AlgoliaAnalytics.trackPageLanding({
      grouped: {
        queryID: result.queryID,
        indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
        objectIDs,
      },
      ungrouped: {
        queryID: result.queryID,
        indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
        objectIDs,
      },
    })

    return result
  }

  async init(appliedOrderBy?: string) {
    await AlgoliaFacetsUtils.fetchFiltersTranslations()
    const searchResponse = await this.initialResponse(appliedOrderBy)
    let promoFilterIsVisible = false
    if (wcs.isInPlp() || wcs.isInSearch()) {
      const espot = await AlgoliaPromoToggles.PLPPromoConfigurationEspot
      promoFilterIsVisible = this.isPromoFiltersDisplayEnabled(espot)
    }
    const allFacets = await AlgoliaFacetsUtils.getFacets({
      promoFilterIsVisible: promoFilterIsVisible,
      searchResponse: searchResponse,
      appliedFacets: this.getAppliedFacets(),
      priceRange: this.priceRange,
    })

    this.firstCallResult = searchResponse
    this.allFacets = allFacets
    this.priceRange = AlgoliaFacetsUtils.mapPriceRange(allFacets)
    this.initialProductHits = searchResponse.hits
    this.totalProductsCount = searchResponse.nbHits
  }

  private isPromoTogglesDisplayEnabled(espot: { [k: string]: boolean }): boolean {
    return espot?.PromoTogglesDisplayEnabled ?? false
  }

  private isPromoFiltersDisplayEnabled(espot: { [k: string]: boolean }): boolean {
    return espot?.PromoFiltersDisplayEnabled ?? false
  }

  async getPromoToggles() {
    if (!this.isInitialized) {
      await this.init()
    }
    return this.firstCallResult
  }

  public getIsPlpTilesExploded(): boolean {
    const value =
      (document.getElementById('isPlpTilesExploded') as HTMLInputElement | undefined)?.value ?? ''
    return value.toLowerCase() === 'true'
  }

  public isPlpExclusive(): boolean {
    const value =
      (document.getElementById('isPlpExclusive') as HTMLInputElement | undefined)?.value ?? ''
    return value.toLowerCase() === 'true'
  }

  async fetchCatalog({
    appliedFacets,
    appliedOrderBy,
    ruleContext,
  }: CatalogParams): Promise<PlpCatalog> {
    if (!this.isInitialized) {
      await this.init(appliedOrderBy)
    }

    const uniqueAppliedFacets = this.sanitizeAppliedFacets(appliedFacets)
    if (this.getIsPlpTilesExploded()) {
      return this.fetchCatalogWithExplodedTiles(uniqueAppliedFacets, appliedOrderBy, ruleContext)
    }

    if (
      (uniqueAppliedFacets && uniqueAppliedFacets.length) ||
      appliedOrderBy ||
      this.getSearchTerm()
    ) {
      return this.fetchCatalogWithAppliedFacets(uniqueAppliedFacets, appliedOrderBy, ruleContext)
    }

    return this.fetchCatalogFromOriginalResponse(ruleContext, appliedOrderBy)
  }

  private sanitizeAppliedFacets(appliedFacets: Array<string>): Array<string> {
    const splitAppliedFacets = appliedFacets.flatMap((facet) => {
      if (Array.isArray(facet)) {
        return facet
      }
      return facet.split(',')
    })
    return splitAppliedFacets.filter((value, index) => splitAppliedFacets.indexOf(value) === index)
  }

  private async fetchCatalogWithExplodedTiles(
    appliedFacets: AppliedFacets,
    appliedOrderBy: undefined | string,
    ruleContext: undefined | string
  ): Promise<PlpCatalog> {
    const facetFilters = AlgoliaFacetsUtils.mergeFacetsForAlgoliaSearch(appliedFacets)

    const queriesCombinations =
      AlgoliaFacetsUtils.generateQueriesCombinations({
        appliedFacets: facetFilters,
        ruleContexts: this.getRuleContexts(ruleContext),
        indexName: appliedOrderBy,
        category: AlgoliaFacetsUtils.getCategoryFacet(),
        searchTerm: this.getSearchTerm() ?? '',
        facetingAfterDistinct: true,
        numericFilters: [
          ...AlgoliaFacetsUtils.applyPriceFilter(appliedFacets, this.priceRange?.max),
          ...AlgoliaFacetsUtils.applyHingeDistanceFilter(appliedFacets),
        ],
      })?.map((el) => el.params) ?? []

    const productsAndFiltersResults = await Algolia.ungroupedSearch<AlgoliaProductHit>({
      sortOrder: appliedOrderBy,
      queries: [
        {
          query: this.getSearchTerm() ?? '',
          ruleContexts: this.getRuleContexts(),
          hitsPerPage: this.hitsPerPage,
          attributesToRetrieve: ['*'],
          facets: ['attributes_translated.*', 'sort*'],
          facetFilters,
          filters: AlgoliaFacetsUtils.getCategoryFacet(),
          facetingAfterDistinct: true,
          numericFilters: [
            ...AlgoliaFacetsUtils.applyPriceFilter(appliedFacets, this.priceRange?.max),
            ...AlgoliaFacetsUtils.applyHingeDistanceFilter(appliedFacets),
          ],
          clickAnalytics: true,
        },
        ...queriesCombinations,
      ],
    })

    const productsResult = productsAndFiltersResults.results[0] as SearchResponse<AlgoliaProductHit>
    const hits = productsResult.hits

    const products: Array<PlpProduct> = hits.map((hit, index) => {
      const carouselOrder = hit?.variants?.length
        ? hit.variants?.map((variant) => ({ productPartNumber: variant.parentPartnumberId }))
        : [{ productPartNumber: hit.parentPartnumberId }]

      return AlgoliaProductsUtils.normalizeProduct({
        product: hit,
        algoliaTracking: {
          grouped: {
            queryID: productsResult.queryID,
            indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
            objectIDs: [hit.objectID],
            position: index,
          },
          ungrouped: {
            queryID: productsResult.queryID,
            indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
            objectIDs: [hit.objectID],
            position: index,
          },
          carouselOrder,
        },
      })
    })

    const objectIDs = hits.map((product) => product.objectID)
    AlgoliaAnalytics.trackFacetChange({
      grouped: {
        queryID: productsResult.queryID,
        indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
        objectIDs,
      },
      ungrouped: {
        queryID: productsResult.queryID,
        indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
        objectIDs,
      },
    })
    const facets = await this.setFacetsUpdate({
      productsResult,
      appliedOrderBy,
      productsAndFiltersResults,
      appliedFacets,
    })
    return {
      facets,
      products,
      totalCount: productsResult.nbHits,
    }
  }

  private async fetchCatalogWithAppliedFacets(
    appliedFacets: AppliedFacets,
    appliedOrderBy: undefined | string,
    ruleContext: undefined | string
  ): Promise<PlpCatalog> {
    const facetFilters = AlgoliaFacetsUtils.mergeFacetsForAlgoliaSearch(appliedFacets)
    const productsAndFiltersResults = await Algolia.multipleGroupedSearch([
      {
        indexName: appliedOrderBy,
        params: {
          query: this.getSearchTerm() ?? '',
          ruleContexts: this.getRuleContexts(ruleContext),
          hitsPerPage: this.hitsPerPage,
          attributesToRetrieve: ['*'],
          facets: ['attributes_translated.*', 'sort*'],
          facetFilters,
          filters: AlgoliaFacetsUtils.getCategoryFacet(),
          facetingAfterDistinct: true,
          numericFilters: [
            ...AlgoliaFacetsUtils.applyPriceFilter(appliedFacets, this.priceRange?.max),
            ...AlgoliaFacetsUtils.applyHingeDistanceFilter(appliedFacets),
          ],
        },
      },
      ...AlgoliaFacetsUtils.generateQueriesCombinations({
        appliedFacets: facetFilters,
        indexName: appliedOrderBy,
        category: AlgoliaFacetsUtils.getCategoryFacet(),
        searchTerm: this.getSearchTerm() ?? '',
        ruleContexts: this.getRuleContexts(),
        facetingAfterDistinct: true,
        numericFilters: [
          ...AlgoliaFacetsUtils.applyPriceFilter(appliedFacets, this.priceRange?.max),
          ...AlgoliaFacetsUtils.applyHingeDistanceFilter(appliedFacets),
        ],
      }),
    ])

    const productsResult = productsAndFiltersResults.results[0] as SearchResponse<AlgoliaProductHit>

    const hits = productsResult.hits
    let products: Array<PlpProduct> = []

    if (hits && hits.length) {
      const queries = hits.map((hit) => ({
        hitsPerPage: this.hitsPerPage,
        query: this.getSearchTerm(),
        ruleContexts: this.getRuleContexts(),
        facetFilters: [...facetFilters, `x_groupkey: ${hit.x_groupkey}`],
        clickAnalytics: true,
        filters: AlgoliaFacetsUtils.getCategoryFacet(),
        numericFilters: [
          ...AlgoliaFacetsUtils.applyPriceFilter(appliedFacets, this.priceRange?.max),
          ...AlgoliaFacetsUtils.applyHingeDistanceFilter(appliedFacets),
        ],
      }))

      const ungroupedSearchResult = await Algolia.ungroupedSearch<AlgoliaProductHit>({
        sortOrder: appliedOrderBy,
        queries,
      })

      const queryID = productsResult.queryID

      products = hits.map((hit, index) => {
        const variants = ungroupedSearchResult.results.find((ungroupedProduct) =>
          ungroupedProduct.hits.some((ungroupedHit) => ungroupedHit.x_groupkey === hit.x_groupkey)
        )?.hits

        const carouselOrder = variants?.length
          ? variants?.map((variant) => ({ productPartNumber: variant.parentPartnumberId }))
          : [{ productPartNumber: hit.parentPartnumberId }]

        return AlgoliaProductsUtils.normalizeProduct({
          product: {
            ...hit,
            variants,
          },
          algoliaTracking: {
            grouped: {
              queryID: productsResult.queryID,
              indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
              objectIDs: variants?.map((hit) => hit.partnumberId),
              position: index,
            },
            ungrouped: {
              queryID: productsResult.queryID,
              indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
              objectIDs: variants?.map((hit) => hit.partnumberId),
              position: index,
            },
            carouselOrder,
          },
        })
      })

      const objectIDs = products
        .map((product) => product.colors.map((color) => color.partNumber))
        .flat()

      AlgoliaAnalytics.trackFacetChange({
        grouped: {
          indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
          queryID,
          objectIDs,
        },
        ungrouped: {
          indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
          queryID,
          objectIDs,
        },
      })
    }
    const facets = await this.setFacetsUpdate({
      productsResult,
      appliedOrderBy,
      productsAndFiltersResults,
      appliedFacets,
    })
    return {
      facets: facets,
      products,
      totalCount: productsResult.nbHits,
    }
  }

  private async fetchCatalogFromOriginalResponse(
    ruleContext: undefined | string,
    appliedOrderBy: undefined | string
  ): Promise<PlpCatalog> {
    let products: Array<PlpProduct> = []

    if (this.initialProductHits.length) {
      const queries = this.initialProductHits.map((hit) => ({
        hitsPerPage: this.hitsPerPage,
        query: this.getSearchTerm(),
        ruleContexts: this.getRuleContexts(ruleContext),
        facetFilters: [`x_groupkey: ${hit.x_groupkey}`],
        filters: AlgoliaFacetsUtils.getCategoryFacet(),
        clickAnalytics: true,
      }))

      const ungroupedSearchResult = await Algolia.ungroupedSearch<AlgoliaProductHit>({
        sortOrder: appliedOrderBy,
        queries,
      })

      const queryID = this.firstCallResult?.queryID
      products = this.initialProductHits.map((hit, index) => {
        const variants = ungroupedSearchResult.results.find((ungroupedProduct) =>
          ungroupedProduct.hits.some((ungroupedHit) => ungroupedHit.x_groupkey === hit.x_groupkey)
        )?.hits
        const carouselOrder = variants?.length
          ? variants?.map((variant) => ({ productPartNumber: variant.parentPartnumberId }))
          : [{ productPartNumber: hit.parentPartnumberId }]

        return AlgoliaProductsUtils.normalizeProduct({
          product: {
            ...hit,
            variants,
          },
          algoliaTracking: {
            grouped: {
              queryID: this.firstCallResult?.queryID,
              indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
              objectIDs: variants?.map((hit) => hit.partnumberId),
              position: index,
            },
            ungrouped: {
              queryID: ungroupedSearchResult.results[0].queryID,
              indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
              objectIDs: variants?.map((hit) => hit.partnumberId),
              position: index,
            },
            carouselOrder,
          },
        })
      })

      const objectIDs = products
        .map((product) => product.colors.map((color) => color.partNumber))
        .flat()

      AlgoliaAnalytics.trackPageLanding({
        grouped: {
          indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
          queryID,
          objectIDs,
        },
        ungrouped: {
          indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
          queryID,
          objectIDs,
        },
      })
    }
    this.facetStats = this.firstCallResult?.facets_stats || ({} as FacetStatsRecordD)
    this.updatePriceRange([], this.facetStats)
    return {
      facets: this.allFacets,
      products,
      totalCount: this.totalProductsCount,
    }
  }

  async fetchMoreProducts({
    appliedFacets = this.getAppliedFacets(),
    appliedOrderBy,
    pageNumber = 1,
  }: CatalogParams): Promise<PlpCatalog> {
    const facetFilters = AlgoliaFacetsUtils.mergeFacetsForAlgoliaSearch(appliedFacets)

    if (this.getIsPlpTilesExploded()) {
      return this.fetchMoreProductsExploded({
        pageNumber,
        facetFilters,
        appliedFacets,
        appliedOrderBy: appliedOrderBy,
      })
    }

    return this.fetchMoreProductsNormally({
      pageNumber,
      facetFilters,
      appliedFacets,
      appliedOrderBy: appliedOrderBy,
    })
  }

  private async fetchMoreProductsExploded({
    pageNumber,
    facetFilters,
    appliedFacets,
    appliedOrderBy,
  }: {
    pageNumber: number
    facetFilters: (string | string[])[]
    appliedFacets: AppliedFacets
    appliedOrderBy?: string
  }): Promise<PlpCatalog> {
    const productsResults = await Algolia.ungroupedSearch<AlgoliaProductHit>({
      sortOrder: appliedOrderBy,
      queries: [
        {
          query: this.getSearchTerm() ?? '',
          ruleContexts: this.getRuleContexts(),
          hitsPerPage: this.hitsPerPage,
          attributesToRetrieve: ['*'],
          facets: ['attributes_translated.*', 'sort*'],
          facetFilters,
          filters: AlgoliaFacetsUtils.getCategoryFacet(),
          numericFilters: [
            ...AlgoliaFacetsUtils.applyPriceFilter(appliedFacets, this.priceRange?.max),
            ...AlgoliaFacetsUtils.applyHingeDistanceFilter(appliedFacets),
          ],
          page: pageNumber,
          clickAnalytics: true,
        },
      ],
    })
    const productsResult = productsResults.results[0] as SearchResponse<AlgoliaProductHit>
    const hits = productsResult.hits

    const queryID = productsResult.queryID

    const products: Array<PlpProduct> = hits.map((hit, index) => {
      const carouselOrder = hit?.variants?.length
        ? hit.variants?.map((variant) => ({ productPartNumber: variant.parentPartnumberId }))
        : [{ productPartNumber: hit.parentPartnumberId }]

      const objectIDs = hit?.variants?.length
        ? hit.variants?.map((variant) => variant.objectID)
        : [hit.objectID]

      return AlgoliaProductsUtils.normalizeProduct({
        product: hit,
        algoliaTracking: {
          grouped: {
            queryID: this.firstCallResult?.queryID,
            indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
            objectIDs,
            position: index,
          },
          ungrouped: {
            queryID: productsResult.queryID,
            indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
            objectIDs,
            position: index,
          },
          carouselOrder,
        },
      })
    })

    const objectIDs = products
      .map((product) => product.colors.map((color) => color.partNumber))
      .flat()

    AlgoliaAnalytics.trackLoadMore({
      grouped: {
        indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
        queryID,
        objectIDs,
      },
      ungrouped: {
        indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
        queryID,
        objectIDs,
      },
    })

    return {
      products,
      totalCount: productsResult.nbHits,
    }
  }

  private async fetchMoreProductsNormally({
    pageNumber,
    facetFilters,
    appliedFacets,
    appliedOrderBy,
  }: {
    pageNumber: number
    facetFilters: (string | string[])[]
    appliedFacets: AppliedFacets
    appliedOrderBy?: string
  }): Promise<PlpCatalog> {
    const groupedSearchResult = await Algolia.groupedSearch<AlgoliaProductHit>({
      query: this.getSearchTerm(),
      index: appliedOrderBy,
      options: {
        clickAnalytics: true,
        attributesToRetrieve: ['*'],
        page: pageNumber,
        facets: ['attributes_translated.*', 'sort*'],
        hitsPerPage: this.hitsPerPage,
        facetFilters,
        filters: AlgoliaFacetsUtils.getCategoryFacet(),
        ruleContexts: this.getRuleContexts(),
        numericFilters: [
          ...AlgoliaFacetsUtils.applyPriceFilter(appliedFacets, this.priceRange?.max),
          ...AlgoliaFacetsUtils.applyHingeDistanceFilter(appliedFacets),
        ],
      },
    })

    const groupedHits = groupedSearchResult.hits
    let products: Array<PlpProduct> = []

    if (groupedHits?.length) {
      const queries = groupedHits.map((groupedHit) => ({
        query: this.getSearchTerm(),
        ruleContexts: this.getRuleContexts(),
        hitsPerPage: this.hitsPerPage,
        facetFilters: [...facetFilters, `x_groupkey: ${groupedHit.x_groupkey}`],
        filters: AlgoliaFacetsUtils.getCategoryFacet(),
        numericFilters: [
          ...AlgoliaFacetsUtils.applyPriceFilter(appliedFacets, this.priceRange?.max),
          ...AlgoliaFacetsUtils.applyHingeDistanceFilter(appliedFacets),
        ],
        clickAnalytics: true,
      }))

      const ungroupedSearchResult = await Algolia.ungroupedSearch<AlgoliaProductHit>({
        sortOrder: appliedOrderBy,
        queries,
      })

      products = groupedHits
        .filter((groupedHit) => !!groupedHit.attributes)
        .map((groupedHit, index) => {
          const carouselOrder = groupedHit?.variants?.length
            ? groupedHit.variants?.map((variant) => ({
                productPartNumber: variant.parentPartnumberId,
              }))
            : [{ productPartNumber: groupedHit.parentPartnumberId }]

          const variants = ungroupedSearchResult.results.find((ungroupedProduct) =>
            ungroupedProduct.hits.some(
              (ungroupedHit) => ungroupedHit.x_groupkey === groupedHit.x_groupkey
            )
          )?.hits

          return AlgoliaProductsUtils.normalizeProduct({
            product: {
              ...groupedHit,
              variants,
            },
            algoliaTracking: {
              grouped: {
                queryID: this.firstCallResult?.queryID,
                indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
                objectIDs: variants?.map((hit) => hit.partnumberId),
                position: index,
              },
              ungrouped: {
                queryID: ungroupedSearchResult.results[0].queryID,
                indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
                objectIDs: variants?.map((hit) => hit.partnumberId),
                position: index,
              },
              carouselOrder,
            },
          })
        })

      const queryID = groupedSearchResult.queryID
      const objectIDs = products
        .map((product) => product.colors.map((color) => color.partNumber))
        .flat()
      AlgoliaAnalytics.trackLoadMore({
        grouped: {
          indexName: Algolia.getGroupedIndexNameOrDefault(appliedOrderBy),
          queryID,
          objectIDs,
        },
        ungrouped: {
          indexName: Algolia.getUngroupedIndexNameOrDefault(appliedOrderBy),
          queryID,
          objectIDs,
        },
      })
    }

    return {
      products,
      totalCount: groupedSearchResult.nbHits,
    }
  }

  facetIsActive = (facetA: string, facetB: string): boolean => {
    return facetA === facetB
  }

  async loadProductsByPageNumber({
    pageNumber,
    dispatch,
  }: {
    pageNumber: number
    nextPageURL: string
    dispatch: (action: string, params?: any) => any
  }) {
    return []
  }

  getPriceSortOptions() {
    return ['__standard_asc', '__standard_desc']
  }

  getSearchTerm = (): undefined | string => {
    const queryParams = new URLSearchParams(window.location.search)
    return queryParams.get(SEARCH_TERM_QUERY_PARAM) ?? undefined
  }

  getRuleContexts = (ruleContext?: string): Array<string> => {
    if (ruleContext) {
      return [ruleContext]
    }
    const queryParams = new URLSearchParams(window.location.search)
    const ruleContextsParam = queryParams.get('ruleContexts') ?? undefined
    const ruleContexts = ruleContextsParam?.split(',') || ruleContextsParam?.split('$facet=')
    return ruleContexts ?? []
  }
  async getCrosslinksFromCategoryNames({
    categoryNames = [],
  }: {
    categoryNames?: string[]
  } = {}) {
    const url = wcs.getEndPoint('trendingFacets')
    const response = await axios.post(url, {
      categoryNames,
    })
    const trendingFacets = response?.data.categorySEODetails?.map(
      (seoDetail: Record<string, { title: string; url: string }>) => {
        const key = Object.keys(seoDetail)[0]
        return {
          categoryName: key,
          href: seoDetail[key].url,
          label: seoDetail[key].title,
        }
      }
    )
    return trendingFacets as Array<{
      categoryName: string
      href: string
      label: string
    }>
  }

  actionsAreVisible() {
    const canShowPLPActionsInputElement = document.getElementById(
      CAN_SHOW_PLP_ACTIONS
    ) as HTMLInputElement
    const plpActionsAreVisible = canShowPLPActionsInputElement?.value || TRUE_LOWERCASE

    return plpActionsAreVisible === TRUE_LOWERCASE
  }

  mapFacets({
    facets,
    appliedFacets,
  }: {
    facets: AllFacets
    appliedFacets: Array<string>
  }): PlpFacet[] {
    const findPrescription = facets[FACET_KEYS.POPULAR_FILTERS]?.entry?.find(
      (e) => e.identifier === FACET_KEYS.PRESCRIPTION_AVAILABLE
    )

    if (
      !findPrescription &&
      FACET_KEYS.HIGH_PRESCRIPTION in facets &&
      AlgoliaFacetsUtils.inViewAllPrescription()
    ) {
      delete facets[FACET_KEYS.HIGH_PRESCRIPTION]
    }
    const mappedFacets = Object.entries(facets).map(([key, facet]) => {
      const { name, entry } = facet

      if (key === FACET_KEYS.PRICE) {
        const indexFacet = appliedFacets.findIndex((el: string) => {
          return new RegExp(PRICE_FILTER).test(el)
        })
        const facetConf = AlgoliaFacetsUtils.getFacetConf(FACET_KEYS.PRICE)
        return {
          label: name,
          key,
          type: 'range',
          layout: 'list',
          showFacet: facetConf?.show(undefined),
          values: [
            {
              count: 0,
              showFacet: true,
              min: Math.round(Number(entry[0]?.entryValue)),
              max: Math.round(Number(entry[1]?.entryValue)),
              preMin: Math.round(Number(this.priceRange?.min)),
              preMax: Math.round(Number(this.priceRange?.max)),
              indexFacet,
            },
          ],
        }
      }

      const layout = this.getFilterLayout(key)
      const type = this.getFilterType(key)
      const facetConf = AlgoliaFacetsUtils.getFacetConf(key)
      return {
        label: name,
        key,
        type,
        layout,
        open: key === FACET_KEYS.POPULAR_FILTERS,
        showFacet: facetConf?.show(entry || []),
        values: (entry || []).map((v) => {
          const { count, label, entryValue, facetDesc, key: filterKey, identifier } = v
          let id = ''
          if (!Array.isArray(v?.entryValue)) {
            id = v?.entryValue?.split(':')[0].split('.')[1] || ''
          }
          const facetConf = AlgoliaFacetsUtils.getFacetConf(id)
          return {
            count: count ? +count : 0,
            label,
            value: entryValue,
            description: facetDesc,
            showFacet: 'showFacet' in v ? v?.showFacet : facetConf?.show(undefined),
            active: appliedFacets?.some((filter) => filter === entryValue),
            icon: filterKey && this.getIcon({ groupKey: key, filterKey, identifier }),
          }
        }),
      }
    })
    return mappedFacets as PlpFacet[]
  }

  private getFilterLayout = (key: string): PlpFilterLayout => {
    if (VERTICAL_FACETS.includes(key)) {
      return 'list'
    }
    return 'grid'
  }

  private getFilterType = (key: string): PlpFilterType => {
    if (key === FACET_KEYS.PRICE) {
      return 'range'
    }
    if (COLOR_FACETS.includes(key)) {
      return 'color'
    }
    if (key === FACET_KEYS.HIGH_PRESCRIPTION) {
      return 'toggle'
    }
    return 'standard'
  }

  private getIcon({
    groupKey,
    filterKey,
    identifier,
  }: {
    groupKey: string
    filterKey: string
    identifier?: string
  }) {
    if (identifier === FACET_KEYS.POLARIZED) {
      return 'polarized-filter'
    }

    if (identifier === FACET_KEYS.CUSTOMIZABLE) {
      return 'customizable'
    }

    if (identifier === FACET_KEYS.PRESCRIPTION_AVAILABLE) {
      return 'filter-prescription'
    }

    if (groupKey === FACET_KEYS.FRAME_SHAPE_FACET) {
      return `frame-shape--${filterKey.toLowerCase()}`
    }
    if (groupKey === FACET_KEYS.FACE_SHAPE) {
      return `face-shape--${filterKey.toLowerCase()}`
    }
    if (groupKey === FACET_KEYS.LENS_COLOR) {
      return `lens-color-${filterKey.toLowerCase()}`
    }
    if (groupKey === FACET_KEYS.FRAME_COLOR) {
      return `frame-color-${filterKey.toLowerCase().replace(' ', '-')}`
    }

    return undefined
  }

  public isPromotionStarted = (startDateTimestamp: number) => {
    if (!startDateTimestamp) return false

    const now = Date.now()
    return startDateTimestamp < now
  }
  public setHitsPerPage(isMobile: boolean | undefined): void {
    this.hitsPerPage = isMobile ? this.hitsPerPageMob : this.hitsPerPageDesk
  }

  private async setFacetsUpdate(conf: {
    productsResult: SearchResponse<AlgoliaProductHit>
    appliedOrderBy: string | undefined
    productsAndFiltersResults: any
    appliedFacets: Array<string>
  }) {
    const { productsResult, appliedOrderBy, productsAndFiltersResults, appliedFacets } = conf
    let promoFilterIsVisible = false
    if (wcs.isInPlp()) {
      const espot = await AlgoliaPromoToggles.PLPPromoConfigurationEspot
      promoFilterIsVisible = this.isPromoFiltersDisplayEnabled(espot)
      this.promoFiltersApplied = {
        sortBadge_Guest: productsResult.facets?.sortBadge_Guest,
        sortBadge_early_access: productsResult.facets?.sortBadge_early_access,
      }
    }
    const newFacets = await AlgoliaFacetsUtils.getFacets({
      promoFilterIsVisible: promoFilterIsVisible,
      searchResponse: productsResult,
      appliedOrderBy,
      appliedFacets,
      priceRange: this.priceRange,
    })
    const facetsWithAdjustedCount = (
      productsAndFiltersResults.results as SearchResponse<AlgoliaProductHit>[]
    )
      .slice(1)
      .map(
        async (facet) =>
          await AlgoliaFacetsUtils.getFacets({
            searchResponse: facet,
            appliedOrderBy,
            appliedFacets,
            priceRange: this.priceRange,
          })
      )

    const mergedFacets = AlgoliaFacetsUtils.mergeOriginalAndNewFacets({
      originalFacets: this.allFacets,
      newFacets,
      promoFiltersApplied: this.promoFiltersApplied,
    })
    const facetsWithAdjustedCountData = (await Promise.all(facetsWithAdjustedCount)) as Facets[]
    const adjustedFacets = AlgoliaFacetsUtils.adjustCountOfSelectedFacets({
      facets: mergedFacets,
      facetsWithAdjustedCount: facetsWithAdjustedCountData,
    })

    const facets = AlgoliaFacetsUtils.disableSiblingsOfSelectedFilters({
      facets: adjustedFacets,
      appliedFacets,
    })
    const lastApplied = appliedFacets[appliedFacets.length - 1]
    if (new RegExp(PRICE_FILTER).test(lastApplied)) {
      this.facetStats = this.firstCallResult?.facets_stats || ({} as FacetStatsRecordD)
    } else {
      this.facetStats = productsResult?.facets_stats || ({} as FacetStatsRecordD)
    }
    this.updatePriceRange(appliedFacets, this.facetStats)

    return facets
  }

  private updatePriceRange(
    appliedFacets: Array<string>,
    facetStats: FacetStatsRecordD | undefined
  ): void {
    const lastApplied = appliedFacets[appliedFacets.length - 1]
    if (
      !facetStats?.sortPrice_Guest ||
      (new RegExp(PRICE_FILTER).test(lastApplied) && appliedFacets.length > 1)
    ) {
      return
    }
    this.priceRange = {
      min: Number(facetStats?.sortPrice_Guest?.min) || 0,
      max: Number(facetStats?.sortPrice_Guest?.max) || 0,
    }
  }
  public setSpeculationRuleMobile(): void {
    const scriptPrerender = document.createElement('script')
    const elementsToPrerender =
      (document.querySelectorAll('a.rb-product-tile__wrapper') as NodeListOf<HTMLAnchorElement>) ||
      []
    const linksToPrerender =
      Array.from(elementsToPrerender)
        ?.splice(0, 4)
        ?.map((el) => el?.href) || []
    scriptPrerender.setAttribute('type', 'speculationrules')
    scriptPrerender.innerHTML = `{
    "prerender": [
        {
            "urls": ${JSON.stringify(linksToPrerender)},
            "eagerness": "immediate"
        }
    ]
}`
    document.head.appendChild(scriptPrerender)
  }
}

window.algoliaPlp = AlgoliaPlp.generateModule()

export default window.algoliaPlp
